import { Injectable, signal, computed } from '@angular/core';
import { Observable, finalize, map, tap } from 'rxjs';
import { HttpService } from 'src/app/core/services/http/http.service';
import { ApiEndPoints, ApiMethod } from 'src/app/core/services/const';
import { DemoService } from 'src/app/modules/auth/services/demo/demo.service';
import {
  AskHelpResponse,
  ChatbotConfirmRequest,
  ChatbotResponse,
  ChatbotSendRequest,
  ConversationTurn,
  DemoAiQuota,
  SubmitHelpFeedback,
  TranscriptResult,
  normalizeChatbotResponse,
  normalizeDemoAiQuota,
  normalizeHelpResponse,
  normalizeTranscript,
} from '../models/help-chat.models';

/**
 * EIN vereintes Chat-Fenster: postet an /api/Chatbot/Send. Der Backend-Chatbot
 * entscheidet per Tool-Auswahl zwischen ERKLAEREN (Help-Q&A, Antwort + Quellen)
 * und HANDELN (Aktions-Tool, Schreib-Vorschlag). Bei einem Vorschlag wird eine
 * Bestaetigen/Abbrechen-Card gezeigt; /api/Chatbot/Confirm fuehrt aus.
 * Feedback laeuft weiterhin ueber /api/Help/Feedback.
 */
@Injectable({ providedIn: 'root' })
export class HelpChatService {
  /** Reactive Conversation-State. Dialog liest via .turns(). */
  private readonly _turns = signal<ConversationTurn[]>([]);
  readonly turns = this._turns.asReadonly();
  readonly isEmpty = computed(() => this._turns().length === 0);

  /** Pending-Flag fuer Loading-State der UI. */
  private readonly _pending = signal(false);
  readonly pending = this._pending.asReadonly();

  /** Konversations-Id fuer Multi-Turn-Kontext (vom Backend zurueckgegeben). */
  private conversationId: string | undefined;

  /**
   * Werkzeug, dessen Ausfuehrung gerade bestaetigt wurde — wandert in den NAECHSTEN
   * Assistant-Turn als {@link ConversationTurn.executedTool} und wird danach geleert.
   * Traeger der Ergebniskarte (B2).
   */
  private pendingExecutedTool: string | undefined;

  /**
   * KI-Kontingent-Stand der laufenden Demo-Session (null = keine Demo → UI blendet Badge/Hinweis aus).
   * Autoritativ vom Server (GET /Chatbot/DemoAiQuota), nach jeder zaehlenden KI-Interaktion aufgefrischt.
   */
  private readonly _demoAiQuota = signal<DemoAiQuota | null>(null);
  readonly demoAiQuota = this._demoAiQuota.asReadonly();

  constructor(
    private http: HttpService,
    private demoService: DemoService,
  ) {}

  /**
   * Frischt den Demo-KI-Kontingent-Stand vom Server auf. No-Op (Signal → null) ausserhalb einer
   * Demo-Session, damit fuer echte Firmen weder ein Call noch eine Anzeige entsteht. Fehler werden
   * still geschluckt — die Kontingent-Anzeige ist rein informativ, die Durchsetzung bleibt serverseitig.
   */
  refreshDemoAiQuota(): void {
    if (!this.demoService.isDemoActive()) {
      this._demoAiQuota.set(null);
      return;
    }
    this.http.requestCall(ApiEndPoints.GetDemoAiQuota, ApiMethod.GET).subscribe({
      next: (raw) => {
        const quota = normalizeDemoAiQuota(raw);
        this._demoAiQuota.set(quota?.isDemo ? quota : null);
      },
      error: () => {
        /* still: Anzeige ist informativ, kein harter Fehler */
      },
    });
  }

  reset(): void {
    this._turns.set([]);
    this.conversationId = undefined;
    this.pendingExecutedTool = undefined;
  }

  /**
   * Stellt eine Frage an den Chatbot.
   *
   * `language` ist der kanonische Short-Code aus dem `LanguageService`.
   *
   * ⚠️ **Das Feld hiess bis zum 10.09.2026 `locale` — und wurde deshalb verworfen.** Das
   * Backend-DTO heisst `ChatbotSendDto.Language`; unbekannte Felder verwirft das
   * Model-Binding stillschweigend. E1 („Antwort in der Sprache der Oberfläche") war damit
   * nur zur Hälfte eingelöst: Die Sprache wurde richtig ermittelt und in ein Feld gelegt,
   * das niemand liest. Am Netzwerk-Mitschnitt gefunden, nicht im Code — ein Feldname auf
   * dem Draht ist für jeden Test in diesem Repo unsichtbar, weil `HttpService.requestCall`
   * ein `Observable<any>` liefert und die Nutzlast nie gegen den Vertrag geprüft wird.
   */
  ask(question: string, language: string = 'de'): Observable<AskHelpResponse> {
    const userTurn: ConversationTurn = { role: 'user', content: question };
    this._turns.update((arr) => [...arr, userTurn]);
    this._pending.set(true);

    const payload: ChatbotSendRequest = {
      message: question,
      language,
      conversationId: this.conversationId,
      // Max 6 letzte Turns ohne den eben hinzugefuegten User-Turn.
      context: this._turns()
        .slice(-7, -1)
        .map((t) => ({ role: t.role, content: t.content })),
    };

    return this.http.requestCall('/Chatbot/Send', ApiMethod.POST, payload).pipe(
      map((raw) => this.applyResponse(raw)),
      // Bei HTTP-Fehler wird applyResponse nicht erreicht -> Pending hier sicher loesen.
      finalize(() => this._pending.set(false)),
    );
  }

  /**
   * Bestaetigt (oder verwirft) einen Schreib-Vorschlag.
   * `choiceLabel` (optional, bereits uebersetzt) wird als eigene User-Bubble in den
   * Verlauf gehaengt, damit der Nutzer SEINE getaetigte Antwort (Ja/Nein) sieht und
   * der Dialog nachvollziehbar bleibt.
   */
  confirm(executionId: string, confirmed: boolean, choiceLabel?: string): Observable<AskHelpResponse> {
    // B2: Nur bei einer BESTAETIGTEN Aktion bekommt die naechste Antwort den Weg zurueck in
    // den Plan. Nach einem Abbruch ist nichts passiert — dort waere der Knopf irrefuehrend.
    this.pendingExecutedTool = confirmed ? this.toolNameFor(executionId) : undefined;
    // Card des betroffenen Turns ausblenden (egal ob Ja/Nein) + Wahl als User-Turn zeigen.
    this._turns.update((arr) => {
      const updated = arr.map((t) =>
        t.proposal?.executionId === executionId || t.proposalBatch?.executionId === executionId
          ? { ...t, proposalHandled: true }
          : t,
      );
      if (choiceLabel) {
        updated.push({ role: 'user', content: choiceLabel });
      }
      return updated;
    });
    this._pending.set(true);

    const payload: ChatbotConfirmRequest = {
      executionId,
      confirmed,
      conversationId: this.conversationId,
    };

    return this.http.requestCall('/Chatbot/Confirm', ApiMethod.POST, payload).pipe(
      map((raw) => this.applyResponse(raw)),
      finalize(() => this._pending.set(false)),
    );
  }

  submitFeedback(feedbackId: string, helpful: boolean, feedbackText?: string): Observable<unknown> {
    const payload: SubmitHelpFeedback = { feedbackId, helpful, feedbackText };
    return this.http.requestCall('/Help/Feedback', ApiMethod.POST, payload);
  }

  /**
   * Sprachaufnahme transkribieren (Speech-to-Text). Sendet das Audio als multipart an
   * /Chatbot/Transcribe; das Backend ruft OpenAI und gibt den Text zurueck. Bei einem
   * fachlichen Fehler liefert das Backend Text="" + eine Notification (HttpService zeigt
   * den Toast bereits selbst) -> hier kommt dann ein leerer String an.
   */
  transcribe(blob: Blob, fileName: string): Observable<TranscriptResult> {
    const fd = new FormData();
    fd.append('audio', blob, fileName);
    return this.http.requestCall('/Chatbot/Transcribe', ApiMethod.POST, fd).pipe(
      // Vertragspunkt 4: `entities` ist optional und heute leer — `normalizeTranscript`
      // liefert dann ein leeres Array, und das Frontend markiert selbst gegen die
      // gecachte Namensliste (Zwischenlösung, siehe util/transcript-entities.ts).
      map((raw: any) => normalizeTranscript(raw)),
      // Transkription zaehlt gegen das Demo-KI-Kontingent → Stand auffrischen.
      tap(() => this.refreshDemoAiQuota()),
    );
  }

  /** Verarbeitet eine Chatbot-Antwort: Assistant-Turn anhaengen, Help-Metadaten mappen. */
  private applyResponse(raw: unknown): AskHelpResponse {
    const empty: AskHelpResponse = {
      answer: '',
      sources: [],
      related: [],
      feedbackId: '',
      fromCache: false,
    };

    const chat: ChatbotResponse | null = normalizeChatbotResponse(raw);
    this._pending.set(false);
    // Chat-Nachricht (ask) zaehlt gegen das Demo-KI-Kontingent → Stand auffrischen. confirm zaehlt
    // zwar nicht, ein Refresh ist aber harmlos und haelt das Badge konsistent.
    this.refreshDemoAiQuota();
    if (!chat) {
      return empty;
    }
    if (chat.conversationId) {
      this.conversationId = chat.conversationId;
    }

    // Help-Antworten tragen Quellen/Related/FeedbackId im Results-Objekt (AskHelpResponseDto).
    const help = chat.results ? normalizeHelpResponse(chat.results) : null;

    // Genau EINEN Turn markieren: die Antwort auf die eben bestaetigte Aktion.
    const executedTool = this.pendingExecutedTool;
    this.pendingExecutedTool = undefined;

    if (chat.reply || chat.proposal || chat.proposalBatch || chat.blocks?.length) {
      this._turns.update((arr) => [
        ...arr,
        {
          role: 'assistant',
          content: chat.reply,
          sources: help?.sources ?? [],
          related: help?.related ?? [],
          feedbackId: help?.feedbackId ?? '',
          proposal: chat.proposal,
          proposalBatch: chat.proposalBatch,
          blocks: chat.blocks,
          noMatch: chat.noMatch || undefined,
          // Ein NEUER Vorschlag in derselben Antwort heisst: es ist noch nichts fertig
          // (Rueckfrage statt Ergebnis) — dann kein Weg-zurueck-Knopf.
          executedTool: chat.proposal || chat.proposalBatch ? undefined : executedTool,
        },
      ]);
    }

    return help ?? { ...empty, answer: chat.reply };
  }

  /** Werkzeugname zu einer ExecutionId aus dem laufenden Verlauf (Einzel- oder Sammelaktion). */
  private toolNameFor(executionId: string): string | undefined {
    for (const t of this._turns()) {
      if (t.proposal?.executionId === executionId) return t.proposal.toolName;
      if (t.proposalBatch?.executionId === executionId) {
        return t.proposalBatch.items[0]?.toolName ?? 'batch';
      }
    }
    return undefined;
  }
}
